import { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  query,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../services/firebase/config";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

function HomeManagement() {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useSelector((state) => state.auth.isAdmin);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [homeData, setHomeData] = useState({
    address: "",
    price: "",
    bedrooms: "",
    bathrooms: "",
    squareFeet: "",
    description: "",
    neighborhood: "",
    type: "sale",
    status: "available",
  });
  const [homeImages, setHomeImages] = useState([]);
  const [homeImage, setHomeImage] = useState(null); // Keep for backward compatibility

  // New state for home editing
  const [activeTab, setActiveTab] = useState(0);
  const [homes, setHomes] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editHomeData, setEditHomeData] = useState(null);
  const [editHomeImages, setEditHomeImages] = useState([]);
  const [editHomeImage, setEditHomeImage] = useState(null); // Keep for backward compatibility
  const [editSelectedUser, setEditSelectedUser] = useState("");
  const [loadingHomes, setLoadingHomes] = useState(true);
  // Search and filter state variables
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  // Filter homes based on search and filter criteria
  const filteredHomes = homes.filter((home) => {
    // Text search filter
    const matchesSearch =
      searchQuery === "" ||
      home.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
      home.neighborhood?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      home.description?.toLowerCase().includes(searchQuery.toLowerCase());

    // Type filter
    const matchesType = filterType === "all" || home.type === filterType;

    // Status filter
    const matchesStatus =
      filterStatus === "all" || home.status === filterStatus;

    return matchesSearch && matchesType && matchesStatus;
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort users alphabetically by email
        const sortedUsers = usersData.sort((a, b) => {
          const emailA = (a.email || "").toLowerCase();
          const emailB = (b.email || "").toLowerCase();
          return emailA.localeCompare(emailB);
        });

        setUsers(sortedUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, []);

  // Fetch homes for the edit tab
  useEffect(() => {
    const fetchHomes = async () => {
      try {
        setLoadingHomes(true);
        const homesSnapshot = await getDocs(collection(db, "homes"));
        const homesData = homesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setHomes(homesData);
      } catch (error) {
        console.error("Error fetching homes:", error);
      } finally {
        setLoadingHomes(false);
      }
    };

    if (activeTab === 1) {
      fetchHomes();
    }
  }, [activeTab]);

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setHomeData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditHomeData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 5 * 1024 * 1024) {
      setHomeImage(file);
    } else {
      alert("Please select an image under 5MB");
    }
  };

  const handleMultipleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => file.size <= 5 * 1024 * 1024);
    if (validFiles.length !== files.length) {
      alert("Some files were too large (over 5MB) and were not selected");
    }
    setHomeImages((prev) => [...prev, ...validFiles]);
  };

  const removeImage = (index) => {
    setHomeImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 5 * 1024 * 1024) {
      setEditHomeImage(file);
    } else {
      alert("Please select an image under 5MB");
    }
  };

  const handleEditMultipleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => file.size <= 5 * 1024 * 1024);
    if (validFiles.length !== files.length) {
      alert("Some files were too large (over 5MB) and were not selected");
    }
    setEditHomeImages((prev) => [...prev, ...validFiles]);
  };

  const removeEditImage = (index, isExisting = false) => {
    if (isExisting) {
      // Remove from existing images in editHomeData
      setEditHomeData((prev) => ({
        ...prev,
        images: prev.images ? prev.images.filter((_, i) => i !== index) : [],
      }));
    } else {
      // Remove from new images being added
      setEditHomeImages((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let imageUrls = [];
      let imageUrl = "";

      // Upload multiple images
      if (homeImages.length > 0) {
        const uploadPromises = homeImages.map(async (image, index) => {
          const storageRef = ref(
            storage,
            `home-images/${Date.now()}_${index}_${image.name}`
          );
          await uploadBytes(storageRef, image);
          return await getDownloadURL(storageRef);
        });
        imageUrls = await Promise.all(uploadPromises);
        imageUrl = imageUrls[0]; // First image for backward compatibility
      }

      // Handle single image for backward compatibility
      if (homeImage && homeImages.length === 0) {
        const storageRef = ref(
          storage,
          `home-images/${Date.now()}_${homeImage.name}`
        );
        await uploadBytes(storageRef, homeImage);
        imageUrl = await getDownloadURL(storageRef);
        imageUrls = [imageUrl];
      }

      // First create the home document
      const homeRef = await addDoc(collection(db, "homes"), {
        ...homeData,
        image: imageUrl, // This is the key field name (backward compatibility)
        images: imageUrls, // New field for multiple images
        createdAt: new Date(),
        addedBy: user.uid,
        price: Number(homeData.price),
        bedrooms: Number(homeData.bedrooms),
        bathrooms: Number(homeData.bathrooms),
        squareFeet: Number(homeData.squareFeet),
        features: [],
        assignedTo: selectedUser || null,
        status: selectedUser ? "occupied" : "available",
      });

      if (selectedUser) {
        // Update user document with consistent image field name
        await updateDoc(doc(db, "users", selectedUser), {
          homeId: homeRef.id,
          homeImage: imageUrl, // Make sure we use the same image URL
          address: homeData.address,
        });
      }

      alert("Home added successfully!");
      setHomeData({
        address: "",
        price: "",
        bedrooms: "",
        bathrooms: "",
        squareFeet: "",
        description: "",
        neighborhood: "",
        type: "sale",
        status: "available",
      });
      setHomeImage(null);
      setHomeImages([]);
      setSelectedUser("");
    } catch (error) {
      console.error("Error adding home:", error);
      alert("Error adding home: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = (home) => {
    setEditHomeData({
      ...home,
      price: home.price?.toString() || "",
      bedrooms: home.bedrooms?.toString() || "",
      bathrooms: home.bathrooms?.toString() || "",
      squareFeet: home.squareFeet?.toString() || "",
      images: home.images || (home.image ? [home.image] : []), // Ensure we have an array
    });
    setEditSelectedUser(home.assignedTo || "");
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditHomeData(null);
    setEditHomeImage(null);
    setEditHomeImages([]);
    setEditSelectedUser("");
  };

  const handleUpdateHome = async () => {
    if (!editHomeData) return;

    setLoading(true);
    try {
      let existingImages =
        editHomeData.images || (editHomeData.image ? [editHomeData.image] : []);
      let newImageUrls = [];
      let imageUrl = editHomeData.image || "";

      // Upload new images if selected
      if (editHomeImages.length > 0) {
        const uploadPromises = editHomeImages.map(async (image, index) => {
          const storageRef = ref(
            storage,
            `home-images/${Date.now()}_${index}_${image.name}`
          );
          await uploadBytes(storageRef, image);
          return await getDownloadURL(storageRef);
        });
        newImageUrls = await Promise.all(uploadPromises);
      }

      // Upload single image if selected (backward compatibility)
      if (editHomeImage) {
        const storageRef = ref(
          storage,
          `home-images/${Date.now()}_${editHomeImage.name}`
        );
        await uploadBytes(storageRef, editHomeImage);
        const singleImageUrl = await getDownloadURL(storageRef);
        newImageUrls.push(singleImageUrl);
        imageUrl = singleImageUrl;
      }

      // Combine existing and new images
      const allImages = [...existingImages, ...newImageUrls];
      if (allImages.length > 0 && !imageUrl) {
        imageUrl = allImages[0]; // Set first image as primary for backward compatibility
      }

      // Check if user assignment has changed
      const previouslyAssignedTo = homes.find(
        (h) => h.id === editHomeData.id
      )?.assignedTo;

      // Update home document
      await updateDoc(doc(db, "homes", editHomeData.id), {
        address: editHomeData.address,
        price: Number(editHomeData.price),
        bedrooms: Number(editHomeData.bedrooms),
        bathrooms: Number(editHomeData.bathrooms),
        squareFeet: Number(editHomeData.squareFeet),
        description: editHomeData.description,
        neighborhood: editHomeData.neighborhood,
        type: editHomeData.type,
        status: editSelectedUser ? "occupied" : editHomeData.status,
        image: imageUrl, // Primary image for backward compatibility
        images: allImages, // All images array
        assignedTo: editSelectedUser || null,
        updatedAt: new Date(),
      });

      // If previously assigned user is different, update old user
      if (previouslyAssignedTo && previouslyAssignedTo !== editSelectedUser) {
        await updateDoc(doc(db, "users", previouslyAssignedTo), {
          homeId: null,
          homeImage: null,
          address: null,
        });
      }

      // If new user is assigned, update user document
      if (editSelectedUser) {
        await updateDoc(doc(db, "users", editSelectedUser), {
          homeId: editHomeData.id,
          homeImage: imageUrl,
          address: editHomeData.address,
        });
      }

      // Refresh homes list
      const homesSnapshot = await getDocs(collection(db, "homes"));
      const homesData = homesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setHomes(homesData);

      alert("Home updated successfully!");
      handleCloseEditDialog();
    } catch (error) {
      console.error("Error updating home:", error);
      alert("Error updating home: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
        >
          <Tab label="Add New Home" />
          <Tab label="Edit Homes" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Add New Home
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={homeData.address}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Price"
                  name="price"
                  type="number"
                  value={homeData.price}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Square Feet"
                  name="squareFeet"
                  type="number"
                  value={homeData.squareFeet}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Bedrooms"
                  name="bedrooms"
                  type="number"
                  value={homeData.bedrooms}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Bathrooms"
                  name="bathrooms"
                  type="number"
                  value={homeData.bathrooms}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={4}
                  value={homeData.description}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Neighborhood"
                  name="neighborhood"
                  value={homeData.neighborhood}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    name="type"
                    value={homeData.type}
                    label="Type"
                    onChange={handleInputChange}
                  >
                    <MenuItem value="sale">For Sale</MenuItem>
                    <MenuItem value="rent">For Rent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={homeData.status}
                    label="Status"
                    onChange={handleInputChange}
                  >
                    <MenuItem value="available">Available</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="sold">Sold</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Assign to User</InputLabel>
                  <Select
                    value={selectedUser}
                    label="Assign to User"
                    onChange={(e) => setSelectedUser(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" component="label" fullWidth>
                    Upload Multiple Home Images
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      multiple
                      onChange={handleMultipleImageChange}
                    />
                  </Button>
                  {homeImages.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Selected Images ({homeImages.length}):
                      </Typography>
                      <Grid container spacing={1}>
                        {homeImages.map((image, index) => (
                          <Grid item key={index} xs={12} sm={6} md={4}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 1,
                                border: "1px solid #ccc",
                                borderRadius: 1,
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{ flex: 1 }}
                              >
                                {image.name}
                              </Typography>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => removeImage(index)}
                                sx={{ ml: 1 }}
                              >
                                Remove
                              </Button>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Box>

                {/* Fallback single image upload for backward compatibility */}
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  Upload Single Image (Legacy)
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
                {homeImage && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Legacy Selected: {homeImage.name}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : "Add Home"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Edit Homes
          </Typography>

          {/* Search and filter controls */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search homes"
                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by address, neighborhood, or description"
                  InputProps={{
                    startAdornment: (
                      <Box
                        component="span"
                        sx={{ color: "text.secondary", mr: 1 }}
                      >
                        🔍
                      </Box>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filterType}
                    label="Type"
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="sale">For Sale</MenuItem>
                    <MenuItem value="rent">For Rent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="available">Available</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="sold">Sold</MenuItem>
                    <MenuItem value="occupied">Occupied</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {loadingHomes ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : homes.length === 0 ? (
            <Typography variant="body1" align="center" my={4}>
              No homes found
            </Typography>
          ) : filteredHomes.length === 0 ? (
            <Typography variant="body1" align="center" my={4}>
              No homes match your search criteria
            </Typography>
          ) : (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Showing {filteredHomes.length} of {homes.length} homes
              </Typography>
              <Grid container spacing={3}>
                {filteredHomes.map((home) => (
                  <Grid item xs={12} sm={6} md={4} key={home.id}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="140"
                        image={
                          home.image ||
                          "https://via.placeholder.com/300x200?text=No+Image"
                        }
                        alt={home.address}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography gutterBottom variant="h6" component="div">
                          {home.address}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Price: {home.price} ND
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Status: {home.status}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {home.bedrooms} bed | {home.bathrooms} bath |{" "}
                          {home.squareFeet} sqft
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleOpenEditDialog(home)}
                          fullWidth
                        >
                          Edit
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Edit Home Dialog */}
          <Dialog
            open={editDialogOpen}
            onClose={handleCloseEditDialog}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>Edit Home</DialogTitle>
            <DialogContent>
              {editHomeData && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="address"
                      value={editHomeData.address}
                      onChange={handleEditInputChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Price"
                      name="price"
                      type="number"
                      value={editHomeData.price}
                      onChange={handleEditInputChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Square Feet"
                      name="squareFeet"
                      type="number"
                      value={editHomeData.squareFeet}
                      onChange={handleEditInputChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Bedrooms"
                      name="bedrooms"
                      type="number"
                      value={editHomeData.bedrooms}
                      onChange={handleEditInputChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Bathrooms"
                      name="bathrooms"
                      type="number"
                      value={editHomeData.bathrooms}
                      onChange={handleEditInputChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      multiline
                      rows={4}
                      value={editHomeData.description}
                      onChange={handleEditInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Neighborhood"
                      name="neighborhood"
                      value={editHomeData.neighborhood}
                      onChange={handleEditInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        name="type"
                        value={editHomeData.type}
                        label="Type"
                        onChange={handleEditInputChange}
                      >
                        <MenuItem value="sale">For Sale</MenuItem>
                        <MenuItem value="rent">For Rent</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="status"
                        value={editHomeData.status}
                        label="Status"
                        onChange={handleEditInputChange}
                      >
                        <MenuItem value="available">Available</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="sold">Sold</MenuItem>
                        <MenuItem value="occupied">Occupied</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Assign to User</InputLabel>
                      <Select
                        value={editSelectedUser}
                        label="Assign to User"
                        onChange={(e) => setEditSelectedUser(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {users.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.email}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    {/* Display existing images */}
                    {editHomeData.images && editHomeData.images.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Current Images ({editHomeData.images.length}):
                        </Typography>
                        <Grid container spacing={2}>
                          {editHomeData.images.map((imageUrl, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Box sx={{ position: "relative" }}>
                                <Box
                                  component="img"
                                  src={imageUrl}
                                  alt={`Home image ${index + 1}`}
                                  sx={{
                                    width: "100%",
                                    height: "150px",
                                    objectFit: "cover",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                  }}
                                />
                                <Button
                                  size="small"
                                  color="error"
                                  variant="contained"
                                  onClick={() => removeEditImage(index, true)}
                                  sx={{
                                    position: "absolute",
                                    top: 5,
                                    right: 5,
                                    minWidth: "auto",
                                    px: 1,
                                  }}
                                >
                                  ×
                                </Button>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}

                    {/* Add new images */}
                    <Box sx={{ mb: 2 }}>
                      <Button variant="contained" component="label" fullWidth>
                        Add More Images
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          multiple
                          onChange={handleEditMultipleImageChange}
                        />
                      </Button>
                      {editHomeImages.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            New Images to Add ({editHomeImages.length}):
                          </Typography>
                          <Grid container spacing={1}>
                            {editHomeImages.map((image, index) => (
                              <Grid item key={index} xs={12} sm={6}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    p: 1,
                                    border: "1px solid #ccc",
                                    borderRadius: 1,
                                    backgroundColor: "#f5f5f5",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{ flex: 1 }}
                                  >
                                    {image.name}
                                  </Typography>
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      removeEditImage(index, false)
                                    }
                                    sx={{ ml: 1 }}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}
                    </Box>

                    {/* Legacy single image upload */}
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ mt: 1 }}
                    >
                      Upload Single Image (Legacy)
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleEditImageChange}
                      />
                    </Button>
                    {editHomeImage && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 1 }}
                      >
                        Selected new image: {editHomeImage.name}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEditDialog}>Cancel</Button>
              <Button
                onClick={handleUpdateHome}
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Update Home"}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      )}
    </Container>
  );
}

export default HomeManagement;
